import * as React from "react";

import { useState, useCallback, useEffect } from "react";
import IconSearch from "../../assets/svg/search-icon-grey.svg";
import { PageTitle } from "../../components/PageTitle";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import toast from "react-hot-toast";
import config from "../../config";
import axiosInstant from "../../api";
import { useUser } from "../../hooks/useUser";
import { ProductCard } from "../admin/products/components/ProductCard";
import { ProductIntelFooter } from "./components/ProductIntelFooter";
import { ProductV2Modal } from "../admin/productsV2/productModal";
import { ProductRunReport } from "../admin/productsV2/productListView/ProductRunReport";
import { WhiteBackground } from "../../components/WhiteBackground";
import { isInternal, canDeleteProduct, productCannotDeleteMessage } from "../../utils";
import { MainContainer } from "../../components/ContentWrapper";
import { ReactComponent as AddIcon } from "../../assets/svg/add-icon.svg";
import { ProductFilters } from "./ProductFilters";
import useDebounce from "../../hooks/useDebounce";
import { NoDataView } from "../../components/EmptyPanel/NoDataView";
import { IconProduct } from "../../utils/icons";
import { LoadMoreBtn, DotLottleLoader, PageDivider } from "../../components/Loader";
import SortControls from "../../components/SortControls/SortControls";
import { useCommonParams } from "../../hooks/useCommonParams";
import { usePagination } from "../../hooks/usePagination";
import { useProductDetailLink } from "../../hooks/useProductDetailLink";
import { useBrandDetailLink } from "../../hooks/useBrandDetailLink";

const PAGE_SIZE = 20;

export default function AllProducts(props) {
    const { goToProduct } = useProductDetailLink();
    const { enabled: brandLinkEnabled, goToBrand } = useBrandDetailLink();
    const { role } = useUser();
    const {
        searchParams,
        setSearchParams,
        page,
        setPage,
        sortType,
        setSortType,
        getSortParam,
        searchText,
        setSearchText
    } = useCommonParams({ defaultSortKey: "dateCreated", sortKeyMap: { name: "title~i" } });
    const [localSearchInput, setLocalSearchInput] = useState(searchParams.get("search") || "");
    const [addProductDialogState, setAddProductDialogState] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(false);
    const [showCannotDeleteDialog, setShowCannotDeleteDialog] = useState(false);
    // { product, pdpUrlId } while the per-URL run icon's report is starting.
    const [runningPdp, setRunningPdp] = useState(null);
    const [cannotDeleteCounts, setCannotDeleteCounts] = useState({ projectCount: 0, videoCount: 0 });
    const debouncedSearchTerm = useDebounce(localSearchInput, 500);

    const onSearchChange = (e) => {
        setLocalSearchInput(e.target.value);
    };

    const handleSelectProduct = (product) => {
        setSelectedProduct(product);
        setAddProductDialogState(true);
        if (product?.desireid) openEditProduct(product);
    };

    const handleSortChange = (newValue) => {
        setSortType({ ...sortType, key: newValue || "" });
    };

    const handleSortDirectionChange = (isAscending) => {
        setSortType({ ...sortType, isAscending });
    };

    useEffect(() => {
        setSearchText(debouncedSearchTerm);
    }, [debouncedSearchTerm]);

    const sortParam = getSortParam();
    const brandFilter = searchParams.get("brand") || "";
    const PRODUCT_QUERY_PARAMS = {
        productCategoryId: "productCategoryId",
        proId: "expert",
        videos: "videos",
        hasPdp: "hasPdp",
        cv: "cv",
    };
    const extraFilters = Object.entries(PRODUCT_QUERY_PARAMS)
        .map(([urlKey, apiKey]) => [apiKey, searchParams.get(urlKey) || ""])
        .filter(([, value]) => value !== "");
    const extraFiltersKey = JSON.stringify(extraFilters);

    const fetchFn = useCallback(
        async (pageNum) => {
            try {
                const params = new URLSearchParams();
                params.set("page", String(pageNum));
                params.set("limit", String(PAGE_SIZE));
                params.set("sort", sortParam);
                // Opt in to the PDP/CV scores, latest reports and resolved
                // manufacturer brand the card footer renders. Off by default
                // on /desires, so creator-facing listings don't pay for it.
                params.set("intel", "true");
                if (searchText) params.set("search", searchText);
                if (brandFilter) {
                    if (brandFilter === "no_brand") {
                        params.set("no_brand", "true");
                    } else {
                        params.set("brand", brandFilter);
                    }
                }
                extraFilters.forEach(([apiKey, value]) => params.set(apiKey, value));
                const url = `${config.API_URL}/desires?${params.toString()}`;
                const response = await axiosInstant.get(url);
                const { rows, count } = response.data;
                return { rows: rows ?? [], count: count ?? 0, error: null };
            } catch (e) {
                const msg = e?.response?.data?.error ?? "Failed to load products";
                toast.error(msg);
                return { rows: [], count: 0, error: msg };
            }
        },
        [searchText, sortParam, brandFilter, extraFiltersKey]
    );

    const {
        items: data,
        totalCount: count,
        loading,
        hasMore,
        canLoadPrevious,
        loadingPrevious,
        error,
        initLoad,
        loadMore,
        loadPrevious,
        topSentinelRef,
        editingItem: editingProduct,
        openEdit: openEditProduct,
        closeEdit: closeEditProduct
    } = usePagination({
        fetchFn,
        pageSize: PAGE_SIZE,
        deepLink: {
            page,
            setPage,
            searchParams,
            setSearchParams,
            ignoreParams: ["page", "view", "edit"]
        },
        editDeepLink: { getId: (p) => p.desireid }
    });

    useEffect(() => {
        if (editingProduct && !addProductDialogState) {
            setSelectedProduct(editingProduct);
            setAddProductDialogState(true);
        }
    }, [editingProduct]);

    const deleteProduct = (desireId) => {
        axiosInstant
            .delete(config.API_URL + "/admin/desires/" + desireId)
            .then(() => {
                toast.success("Product deleted successfully!");
                setSelectedProduct(false);
                setAddProductDialogState(false);
                initLoad(page);
            })
            .catch((error) => {
                if (error.response?.status === 409) {
                    const body = error.response.data || {};
                    setCannotDeleteCounts({
                        projectCount: body.projectCount || 0,
                        videoCount: body.videoCount || 0,
                    });
                    setShowCannotDeleteDialog(true);
                } else {
                    toast.error("Something went wrong");
                    setSelectedProduct(false);
                    setAddProductDialogState(false);
                }
            });
    };

    const onComplete = () => {
        setAddProductDialogState(false);
        closeEditProduct();
        initLoad(page);
    };

    // Cards carry pdp_report_running / cv_running; reload while any product
    // on the page is analyzing so the spinners clear and scores fill in on
    // their own (same cadence as the Brands page).
    const anyIntelRunning = data.some((p) => p.pdp_report_running || p.cv_running || p.aip_running);
    useEffect(() => {
        if (!anyIntelRunning) return undefined;
        const t = setInterval(() => initLoad(page), 8000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyIntelRunning]);

    return (
        <WhiteBackground>
            <MainContainer>
                <div className="flex flex-wrap justify-between gap-3">
                    <PageTitle title={"PRODUCTS"} />

                    {isInternal(role) && (
                        <div className="flex items-center">
                            <button
                                onClick={() => {
                                    setAddProductDialogState(true);
                                    setSelectedProduct(false);
                                }}
                                className="flex items-center gap-1 bg-accent text-base text-white mx-auto py-3 px-5 lg:px-10 cursor-pointer rounded-xl focus:outline-none focus:shadow-outline"
                            >
                                <AddIcon className="mr-1" />
                                New Product
                            </button>
                        </div>
                    )}
                </div>
                <div className="pt-8 pb-3">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 sm:gap-[91px]">
                        <div className="flex justify-center w-full sm:w-auto">
                            <div className="w-full relative">
                                <img
                                    className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
                                    src={IconSearch}
                                />
                                <input
                                    type="text"
                                    className="w-full sm:w-[333px] h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
                                    id="search-experts"
                                    placeholder="Search"
                                    value={localSearchInput}
                                    onChange={onSearchChange}
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 items-center flex-wrap">
                            <SortControls
                                showSelect
                                sortKey={sortType.key}
                                isAscending={sortType.isAscending}
                                onSortKeyChange={handleSortChange}
                                onDirectionChange={handleSortDirectionChange}
                                options={[
                                    { value: "dateCreated", label: "Created Date" },
                                    { value: "name", label: "Name" },
                                    { value: "desireid", label: "ID Number" }
                                ]}
                            />
                            <ProductFilters
                                shouldShowFilter={isInternal(role)}
                                onSearchUpdate={(e) => console.log(e)}
                            />
                        </div>
                    </div>
                </div>

                <PageDivider
                    count={count}
                    label="Products"
                    className="mt-5"
                    canLoadPrevious={canLoadPrevious}
                    hasMore={hasMore}
                    onLoadPrevious={loadPrevious}
                />

                <div className="mt-5 mb-4">
                    {loading && data.length === 0 && <DotLottleLoader fill={false} />}

                    {!loading && data.length === 0 && !error && (
                        <NoDataView
                            text="No products found"
                            subText="Your search does not match any existing products. "
                            Icon={IconProduct}
                            iconProps={{ shape: "square" }}
                        />
                    )}

                    {data.length > 0 && (
                        <>
                            {canLoadPrevious && <div ref={topSentinelRef} style={{ height: 1 }} />}
                            {loadingPrevious && (
                                <div className="flex justify-center py-2">
                                    <DotLottleLoader fill={false} sx={{ width: 100, height: 100 }} />
                                </div>
                            )}
                            <div
                                className="my-8 grid gap-4"
                                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
                            >
                                {data.map((product) => (
                                    <ProductCard
                                        key={product.id}
                                        actions={{
                                            onEdit: () => handleSelectProduct(product),
                                            ...(canDeleteProduct(role) && {
                                                onDelete: () => deleteProduct(product.desireid)
                                            })
                                        }}
                                        image={product.image}
                                        onClick={() => goToProduct(product)}
                                        brands={product?.brands}
                                        productId={product.desireid}
                                        title={product.title}
                                        description={product.short_description}
                                        videoCount={product.video_count ?? product.productVideos?.length}
                                        pdpUrls={product.pdp_urls}
                                        onBrandClick={brandLinkEnabled ? goToBrand : undefined}
                                        onRunPdp={
                                            isInternal(role)
                                                ? (u) => setRunningPdp({ product, pdpUrlId: u.id })
                                                : undefined
                                        }
                                        pdpReportRunning={product.pdp_report_running}
                                        intel={
                                            isInternal(role) ? (
                                                <ProductIntelFooter
                                                    product={product}
                                                    onChanged={() => initLoad(page)}
                                                />
                                            ) : undefined
                                        }
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {data.length > 0 && hasMore && <LoadMoreBtn onClick={loadMore} loading={loading} />}
            </MainContainer>

            {addProductDialogState && (
                <ProductV2Modal
                    product={selectedProduct || undefined}
                    onClose={() => {
                        setAddProductDialogState(false);
                        closeEditProduct();
                    }}
                    onComplete={() => onComplete()}
                />
            )}

            {runningPdp && (
                <ProductRunReport
                    product={runningPdp.product}
                    brandId={runningPdp.product.intel_brand_id ?? null}
                    pdpUrlId={runningPdp.pdpUrlId}
                    onClose={() => setRunningPdp(null)}
                    onStarted={() => initLoad(page)}
                />
            )}

            <ConfirmationDialog
                open={showCannotDeleteDialog}
                onClose={() => setShowCannotDeleteDialog(false)}
                onConfirm={() => setShowCannotDeleteDialog(false)}
                title="Delete Product"
                description={productCannotDeleteMessage(cannotDeleteCounts)}
                confirmLabel="Continue"
                cancelLabel={null}
                showDivider={false}
            />
        </WhiteBackground>
    );
}
